import type React from 'react';
import {
  X,
  CreditCard,
  Calendar,
  FileText,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  User,
  Building2,
  Ban,
  Send,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PaymentBatch } from '../../types';
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  PAYMENT_METHOD_LABELS,
} from '../../types';

function StatusIcon({ status }: { readonly status: PaymentBatch['status'] }) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'failed':
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    case 'processing':
      return <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />;
    case 'approved':
      return <CheckCircle className="h-5 w-5 text-blue-500" />;
    case 'pending_approval':
      return <Clock className="h-5 w-5 text-amber-500" />;
    default:
      return <FileText className="h-5 w-5 text-gray-500" />;
  }
}

interface PaymentDetailModalProps {
  readonly batch: PaymentBatch | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onApprove: (batchId: string) => void;
  readonly onProcess: (batchId: string) => void;
  readonly onReject: (batchId: string) => void;
  readonly onRetry: (batchId: string) => void;
  readonly isApproving?: boolean;
  readonly isProcessing?: boolean;
}

type WorkflowStage = 'draft' | 'approval' | 'processing' | 'complete';

interface StageStyle {
  textColor: string;
  bgColor: string;
  connectorColor: string;
}

function getStageStyle(stage: WorkflowStage, status: PaymentBatch['status']): StageStyle {
  const completedStatuses: Record<WorkflowStage, PaymentBatch['status'][]> = {
    draft: ['pending_approval', 'approved', 'processing', 'completed', 'failed'],
    approval: ['approved', 'processing', 'completed'],
    processing: ['processing', 'completed'],
    complete: ['completed'],
  };

  const activeStatuses: Record<WorkflowStage, PaymentBatch['status'][]> = {
    draft: [],
    approval: ['pending_approval'],
    processing: ['approved'],
    complete: [],
  };

  if (stage === 'complete' && status === 'failed') {
    return {
      textColor: 'text-red-600',
      bgColor: 'bg-red-100',
      connectorColor: 'bg-red-500' 
    };
  }
  if (completedStatuses[stage].includes(status)) {
    return {
      textColor: 'text-green-600',
      bgColor: 'bg-green-100',
      connectorColor: 'bg-green-500' 
    };
  }
  if (activeStatuses[stage].includes(status)) {
    const colorMap: Record<string, StageStyle> = {
      pending_approval: {
        textColor: 'text-amber-600',
        bgColor: 'bg-amber-100',
        connectorColor: 'bg-muted' 
      },
      approved: {
        textColor: 'text-blue-600',
        bgColor: 'bg-blue-100',
        connectorColor: 'bg-muted' 
      },
    };
    return colorMap[status] ?? {
      textColor: 'text-muted-foreground',
      bgColor: 'bg-muted',
      connectorColor: 'bg-muted' 
    };
  }
  return {
    textColor: 'text-muted-foreground',
    bgColor: 'bg-muted',
    connectorColor: 'bg-muted' 
  };
}

function PaymentWorkflowStep({
  stage,
  label,
  icon,
  status,
}: {
  readonly stage: WorkflowStage;
  readonly label: string;
  readonly icon: React.ReactNode;
  readonly status: PaymentBatch['status'];
}) {
  const style = getStageStyle(stage, status);
  return (
    <div className={`flex flex-col items-center gap-1 ${style.textColor}`}>
      <div className={`rounded-full p-2 ${style.bgColor}`}>
        {icon}
      </div>
      <span className="text-xs">{label}</span>
    </div>
  );
}

function WorkflowConnector({
  stage, status 
}: {
  readonly stage: WorkflowStage;
  readonly status: PaymentBatch['status'] 
}) {
  const style = getStageStyle(stage, status);
  return <div className={`flex-1 h-0.5 mx-2 ${style.connectorColor}`} />;
}

export function PaymentDetailModal({
  batch,
  isOpen,
  onClose,
  onApprove,
  onProcess,
  onReject,
  onRetry,
  isApproving,
  isProcessing,
}: PaymentDetailModalProps) {
  if (!isOpen || !batch) return null;

  const statusColor = PAYMENT_STATUS_COLORS[batch.status];
  const canApprove = batch.status === 'pending_approval';
  const canProcess = batch.status === 'approved';
  const canReject = batch.status === 'pending_approval' || batch.status === 'draft';
  const canRetry = batch.status === 'failed';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-background p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-primary/10 p-3">
              <CreditCard className="h-8 w-8 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{batch.batchNumber}</h2>
                <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor}`}>
                  {PAYMENT_STATUS_LABELS[batch.status]}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <StatusIcon status={batch.status} />
                <span>{PAYMENT_METHOD_LABELS[batch.paymentMethod]}</span>
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Payment Summary */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border p-4">
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Schedule Details
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Scheduled Date</span>
                  <span>{new Date(batch.scheduledDate).toLocaleDateString()}</span>
                </div>
                {batch.processedDate && (
                  <div className="flex justify-between text-green-600">
                    <span>Processed Date</span>
                    <span>{new Date(batch.processedDate).toLocaleDateString()}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created</span>
                  <span>{new Date(batch.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4">
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                Workflow
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created By</span>
                  <span>{batch.createdBy}</span>
                </div>
                {batch.approvedBy && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Approved By</span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {batch.approvedBy}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Flow Status */}
          <div className="rounded-lg border p-4">
            <h3 className="font-medium mb-4">Payment Workflow</h3>
            <div className="flex items-center justify-between">
              <PaymentWorkflowStep stage="draft" label="Draft" icon={<FileText className="h-5 w-5" />} status={batch.status} />
              <WorkflowConnector stage="draft" status={batch.status} />
              <PaymentWorkflowStep stage="approval" label="Approval" icon={<Clock className="h-5 w-5" />} status={batch.status} />
              <WorkflowConnector stage="approval" status={batch.status} />
              <PaymentWorkflowStep stage="processing" label="Processing" icon={<Send className="h-5 w-5" />} status={batch.status} />
              <WorkflowConnector stage="processing" status={batch.status} />
              <PaymentWorkflowStep
                stage="complete"
                label={batch.status === 'failed' ? 'Failed' : 'Complete'}
                icon={batch.status === 'failed' ? <AlertCircle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
                status={batch.status}
              />
            </div>
          </div>

          {/* Invoices in Batch */}
          <div>
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              Invoices in Batch ({batch.invoiceCount})
            </h3>
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Invoice</th>
                    <th className="px-4 py-2 text-left font-medium">Supplier</th>
                    <th className="px-4 py-2 text-right font-medium">Due Date</th>
                    <th className="px-4 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {batch.items.map((item) => (
                    <tr key={item.invoiceId} className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-primary">{item.invoiceNumber}</td>
                      <td className="px-4 py-3">{item.supplierName}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {new Date(item.dueDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        ${item.amount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="rounded-lg border p-4">
            <div className="flex justify-between items-center">
              <span className="font-medium">Total Payment Amount</span>
              <div className="flex items-center gap-1 text-xl font-bold text-primary">
                <DollarSign className="h-5 w-5" />
                {batch.totalAmount.toLocaleString()}
                <span className="text-sm text-muted-foreground font-normal ml-1">
                  {batch.currency}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {batch.notes && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2 text-amber-800">
                <AlertCircle className="h-4 w-4" />
                Notes
              </h3>
              <p className="text-sm text-amber-700">{batch.notes}</p>
            </div>
          )}
        </div>

        {/* Actions Footer */}
        <div className="sticky bottom-0 border-t bg-background p-4">
          <div className="flex flex-wrap gap-2 justify-end">
            {canReject && (
              <Button variant="outline" onClick={() => onReject(batch.batchId)}>
                <Ban className="mr-2 h-4 w-4" />
                Reject
              </Button>
            )}
            {canRetry && (
              <Button variant="outline" onClick={() => onRetry(batch.batchId)}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Retry Payment
              </Button>
            )}
            {canProcess && (
              <Button
                variant="outline"
                onClick={() => onProcess(batch.batchId)}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Release Payment
              </Button>
            )}
            {canApprove && (
              <Button onClick={() => onApprove(batch.batchId)} disabled={isApproving}>
                {isApproving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Approve Batch
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
